import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { assetUrl, errMsg } from "@/lib/api";
import { ModelPicker, usd } from "./ModelPicker";
import EstimateNotes from "@/components/ai/EstimateNotes";

const ASPECTS = [["16:9", "Panoramica 16:9"], ["4:3", "Orizzontale 4:3"], ["1:1", "Quadrata 1:1"], ["3:4", "Verticale 3:4"]];

export default function AiImageTab({ api, projectId, initialPrompt, onPick, setAssets }) {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const [prompt, setPrompt] = useState(initialPrompt || "");
  const [aspect, setAspect] = useState("4:3");
  const [ok, setOk] = useState(false);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/media/models").then(({ data }) => { setModels(data); setModel((m) => m || (data.find((x) => x.default) || data[0])?.id || ""); }).catch(() => {}); }, [api]);
  const [est, setEst] = useState(null);
  useEffect(() => { setEst(null); if (!model) return; api.post(`/projects/${projectId}/media/estimate`, { model }).then(({ data }) => setEst(data.estimate)).catch(() => {}); }, [api, projectId, model]);
  useEffect(() => { setOk(false); }, [model, prompt]);

  const generate = async () => {
    setResult(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/media/generate`, { prompt, model, aspect, confirm_usd: est?.usd });
      setJob({ id: data.job_id, status: "running" });
      const tick = async () => {
        const { data: j } = await api.get(`/jobs/${data.job_id}`);
        if (j.status === "running") { setTimeout(tick, 2500); return; }
        setJob(j);
        if (j.status === "done") { setResult(j.result.asset); setAssets?.((xs) => [j.result.asset, ...xs]); toast.success("Immagine generata"); }
        else toast.error(j.error || "Generazione non riuscita");
      };
      setTimeout(tick, 2500);
    } catch (e) { toast.error(errMsg(e)); setJob(null); }
  };
  const running = job?.status === "running";

  return (
    <div className="space-y-4" data-testid="image-picker-ai">
      <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="Descrivi la foto: soggetto, ambiente, luce. Es. sala relax minimal con legno chiaro e luce naturale"
        data-testid="ai-image-prompt" />
      <ModelPicker models={models} value={model} onChange={setModel} />
      <div className="flex flex-wrap items-center gap-3">
        <Select value={aspect} onValueChange={setAspect}>
          <SelectTrigger className="w-48" data-testid="ai-image-aspect"><SelectValue /></SelectTrigger>
          <SelectContent>{ASPECTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="ai-image-confirm">
          <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="ai-image-confirm-checkbox" />
          Confermo il costo stimato di <strong className="tabular-nums">{est ? usd(est.usd) : "…"}</strong>
        </label>
      </div>
      <EstimateNotes est={est} testid="ai-image-estimate" />
      <Button onClick={generate} disabled={!ok || !est || !!est.blocked || prompt.trim().length < 3 || running} className="bg-sky-600 hover:bg-sky-700" data-testid="ai-image-generate-btn">
        {running ? <><Loader2 size={14} className="mr-1.5 animate-spin" />Generazione in corso (10-40 s)…</> : <><Sparkles size={14} className="mr-1.5" />Genera immagine</>}
      </Button>
      {result && (
        <div className="rounded-xl border p-3 flex flex-col sm:flex-row gap-3 items-start" data-testid="ai-image-result">
          <img src={assetUrl(result.url)} alt="" className="w-full sm:w-64 rounded-lg object-cover" />
          <div className="space-y-2">
            <p className="text-sm text-slate-700">Salvata negli asset del progetto (cartella AI).</p>
            <Button size="sm" onClick={() => onPick(result.url, { provider: "ai" })} data-testid="ai-image-use-btn">Usa questa immagine</Button>
          </div>
        </div>
      )}
    </div>
  );
}
